import type { LucideIcon } from 'lucide-react'
import {
  Archive,
  BarChart3,
  BookOpen,
  Building2,
  CalendarClock,
  Download,
  FileText,
  Kanban,
  Lightbulb,
  ListVideo,
  MessageSquare,
  Mic,
  Plus,
  Radio,
  Settings,
  Sparkles,
  Star,
  Upload,
  Users,
} from 'lucide-react'
import type { CurrentUser, Role } from '@/types/user'

export interface NavItem {
  label: string
  to: string
  icon: LucideIcon
  /** Roles allowed to see the item. Omit for everyone. */
  roles?: Role[]
  /** Key of a live count to show as a badge. */
  badge?: 'videoList' | 'dueToday' | 'inbox' | 'ideas'
  end?: boolean
}

export interface NavGroup {
  title: string
  items: NavItem[]
}

const STAFF: Role[] = ['admin', 'sub-admin', 'team-lead', 'yt-helper-lead', 'qa', 'yt-helper', 'editor']
const LEADS: Role[] = ['admin', 'sub-admin', 'team-lead', 'yt-helper-lead']

/** AI Studio is parked for now (per Phil, Oct 2026). Flip this on to show it in the sidebar again. */
export const SHOW_AI_STUDIO = false

export const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Workspace',
    items: [
      { label: 'Production Board', to: '/', icon: Kanban, end: true },
      { label: 'Dashboard', to: '/dashboard', icon: BarChart3, roles: ['admin', 'sub-admin', 'team-lead', 'customer'] },
      { label: 'Video List', to: '/video-list', icon: ListVideo, roles: LEADS, badge: 'videoList' },
      { label: 'Due Today', to: '/due-cards', icon: CalendarClock, roles: STAFF, badge: 'dueToday' },
    ],
  },
  {
    title: 'Production',
    items: [
      { label: 'New Request', to: '/new-request', icon: Plus, roles: ['admin', 'customer'] },
      { label: 'Upload Footage', to: '/upload', icon: Upload, roles: STAFF },
      { label: 'Archived', to: '/archived', icon: Archive, roles: LEADS },
      { label: 'Export', to: '/export', icon: Download, roles: ['admin', 'sub-admin'] },
    ],
  },
  {
    title: 'AI Studio',
    items: [
      { label: 'Studio home', to: '/studio', icon: Sparkles, roles: ['admin', 'customer'], end: true },
      { label: 'Ideas board', to: '/studio/ideas', icon: Lightbulb, roles: ['admin', 'customer'], badge: 'ideas' },
      { label: 'Script writer', to: '/studio/script', icon: FileText, roles: ['admin', 'customer'] },
      { label: 'Record & AI Direct', to: '/studio/record', icon: Mic, roles: ['admin', 'customer'] },
      { label: 'Brand voice', to: '/studio/brand-voice', icon: Radio, roles: ['admin', 'customer'] },
    ],
  },
  {
    title: 'People',
    items: [
      { label: 'Customers', to: '/customers', icon: Building2, roles: STAFF },
      { label: 'Team', to: '/team', icon: Users, roles: LEADS },
      { label: 'Ratings', to: '/ratings', icon: Star, roles: ['admin', 'sub-admin'] },
    ],
  },
  {
    title: 'Resources',
    items: [
      { label: 'Support Inbox', to: '/inbox', icon: MessageSquare, badge: 'inbox' },
      { label: 'Client Handbook', to: '/handbook', icon: BookOpen },
      { label: 'Portal Settings', to: '/settings', icon: Settings, roles: ['admin'] },
    ],
  },
]

export function navGroupsFor(user: CurrentUser | null): NavGroup[] {
  if (!user) return []
  return NAV_GROUPS.filter((g) => SHOW_AI_STUDIO || g.title !== 'AI Studio')
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => !item.roles || item.roles.includes(user.client_type)),
    }))
    .filter((g) => g.items.length > 0)
}

/** Flat lookup used for breadcrumbs / page titles. */
export function findNavItem(pathname: string): NavItem | undefined {
  const items = NAV_GROUPS.flatMap((g) => g.items)
  return items.find((i) => (i.end ? i.to === pathname : pathname === i.to || pathname.startsWith(i.to + '/')))
}
